import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoginService from '../../services/LoginService';
import Alert from '../common/Alert';
import LanguageSelector from '../common/LanguageSelector';
import DynamicLogo from '../common/DynamicLogo';
import PasswordInputField from '../common/PasswordInputField';

const Login = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    if (rememberedUsername) {
      setUsername(rememberedUsername);
      setRememberMe(true);
    }

    // Redirigir al dashboard si ya está autenticado
    if (localStorage.getItem('authToken')) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Limpiar errores anteriores
    setError(null);
    setFieldErrors({});

    // Validar campos vacíos
    const errors = {};
    if (!username) {
      errors.username = t('validation.usernameRequired');
    }
    if (!password) {
      errors.password = t('validation.passwordRequired');
    } else if (password.length < 3) {
      errors.password = 'Password must be at least 3 characters long.';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError(t('validation.pleaseFillRequired'));
      return;
    }

    setIsLoading(true);

    try {
      const data = await LoginService.login(username, password);

      if ((typeof data === 'object' && data !== null && data.status === false) || !data?.token) {
        // ... (mensaje de error)
        return;
      }

      if (rememberMe) {
        localStorage.setItem('rememberedUsername', username);
      } else {
        localStorage.removeItem('rememberedUsername');
      }

      // Redirigir al dashboard - el servicio ya guardó los datos
      navigate('/dashboard');
    } catch (err) {
      let userFriendlyMessage = t('login.errors.unableToSignIn');

      const status = err?.response?.status;
      const rawMessage = err?.response?.data?.message || err?.response?.data?.error || err?.response?.raw || err?.message || '';
      const normalizedMessage = rawMessage.toLowerCase();

      const isCredentialIssue =
        normalizedMessage.includes('credential') ||
        normalizedMessage.includes('usuario') ||
        normalizedMessage.includes('user') ||
        normalizedMessage.includes('password') ||
        normalizedMessage.includes('contrase') ||
        normalizedMessage.includes('login') ||
        normalizedMessage.includes('auth');

      if (status === 401 || status === 403 || status === 400 || isCredentialIssue) {
        userFriendlyMessage = t('login.errors.badCredentials');
      } else if (!navigator.onLine) {
        userFriendlyMessage = t('login.errors.noConnection');
      } else if (err?.message && err.message.toLowerCase().includes('network')) {
        userFriendlyMessage = t('login.errors.networkError');
      } else if (status === 500) {
        userFriendlyMessage = t('login.errors.serverError');
      }

      setError(userFriendlyMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="modern-login-page">
      <div className="login-container">
        {/* Language Selector */}
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 10 }}>
          <LanguageSelector />
        </div>

        <div className="login-card">
          {/* Logo */}
          <div className="login-logo">
            <DynamicLogo alt="Logo" />
          </div>

          {/* Header */}
          <div className="login-header">
            <h1 className="login-title">{t('login.title')}</h1>
            <p className="login-subtitle">{t('login.subtitle')}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="login-form">
            {/* Error Message */}
            <Alert 
              type="error"
              message={error}
              onClose={() => setError(null)}
              autoClose={3000}
            />

            {/* Username Field */}
            <div className="login-form-group">
              <label htmlFor="username">
                <i className="fa fa-user"></i> {t('login.username')}
              </label>
              <div className="login-input-wrapper">
                <input
                  id="username"
                  name="username"
                  type="text"
                  className={`form-control login-input ${fieldErrors.username ? 'has-error' : ''}`}
                  placeholder={t('login.enterUsername')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  aria-invalid={!!fieldErrors.username}
                />
              </div>
              {fieldErrors.username && (
                <div className="field-error-message">
                  {fieldErrors.username}
                </div>
              )}
            </div>

            {/* Password Field */}
            <div className="login-form-group">
              <label htmlFor="password">
                <i className="fa fa-lock"></i> {t('login.password')}
              </label>
              <PasswordInputField
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.enterPassword')}
                className={`login-input ${fieldErrors.password ? 'has-error' : ''}`}
                wrapperClassName="login-input-wrapper"
                disabled={isLoading}
                ariaInvalid={!!fieldErrors.password}
                autoComplete="current-password"
                toggleLabels={{
                  show: t('common.showPassword'),
                  hide: t('common.hidePassword'),
                }}
              />
              {fieldErrors.password && (
                <div className="field-error-message">
                  {fieldErrors.password}
                </div>
              )}
            </div>

            {/* Remember Me */}
            <div className="login-remember">
              <label className="remember-checkbox">
                <input
                  id="RememberMe"
                  name="rememberme"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isLoading}
                />
                <span>{t('login.rememberMe')}</span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="login-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <i className="fa fa-spinner fa-spin"></i> {t('login.signingIn')}
                </>
              ) : (
                <>
                  <i className="fa fa-sign-in"></i> {t('login.signIn')}
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="login-footer">
          {t('common.copyright')}
        </p>
      </div>
    </section>
  );
};

export default Login;
